import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Radar, Loader2, Check, ArrowUpRight, Eye, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt$ = (n) => n >= 1_000_000 ? `$${(n / 1_000_000).toFixed(2)}M` : `$${(n || 0).toLocaleString()}`;

export default function Scouter() {
  const { user, loading, authHeaders } = useAuth();
  const [leads, setLeads] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [corridor, setCorridor] = useState('Both');
  const [minRoi, setMinRoi] = useState(15);
  const [filter, setFilter] = useState('all');

  const loadLeads = async () => {
    try {
      const { data } = await axios.get(`${API}/scouter/leads`, {
        headers: authHeaders(), withCredentials: true,
      });
      setLeads(data);
    } catch (e) { console.error("Silent failure:", e); }
  };

  useEffect(() => { if (user?.role === 'admin') loadLeads(); /* eslint-disable-next-line */ }, [user]);

  if (loading) return <div className="px-6 md:px-24 py-32 text-center text-[#A3A3A3]">Verifying admiral seal…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== 'admin') {
    return (
      <div className="px-6 md:px-12 lg:px-24 py-24">
        <Radar size={40} className="text-[#D4AF37] mb-8" strokeWidth={1} />
        <h1 className="text-4xl font-extralight mb-4">Scouter access restricted.</h1>
        <p className="text-[#A3A3A3]">This bridge belongs to Mercy.</p>
      </div>
    );
  }

  const runScan = async () => {
    setScanning(true);
    try {
      const { data } = await axios.post(`${API}/scouter/scan`,
        { corridor, min_roi_pct: Number(minRoi), limit: 5 },
        { headers: authHeaders(), withCredentials: true });
      toast.success(`Scouter ingested ${data.ingested} leads.`);
      await loadLeads();
    } catch {
      toast.error('Scouter feed corrupted. Retry.');
    } finally {
      setScanning(false);
    }
  };

  const updateStatus = async (leadId, status) => {
    try {
      await axios.patch(`${API}/scouter/leads/${leadId}/status`,
        { status }, { headers: authHeaders(), withCredentials: true });
      await loadLeads();
    } catch { toast.error('Status update failed.'); }
  };

  const filtered = filter === 'all' ? leads : leads.filter((l) => l.status === filter);

  return (
    <div className="px-6 md:px-12 lg:px-24 py-16 min-h-[80vh]">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-12">
        <div className="md:col-span-7">
          <div className="text-[10px] tracking-[0.4em] uppercase text-[#D4AF37] mb-6 flex items-center gap-3">
            <Radar size={14} strokeWidth={1.5} /> AI Listing Scouter
          </div>
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-extralight leading-[1] tracking-tight">
            Autonomous<br />
            <span className="font-display italic text-[#D4AF37]">lead corridor.</span>
          </h1>
        </div>
        <div className="md:col-span-5 flex items-end">
          <p className="text-base text-[#A3A3A3] leading-relaxed font-light">
            MLS · Zillow · Off-Market · Dark Signal · Diaspora Source.
            The Scouter runs autonomous passes and surfaces only deals above the ROI cutoff.
          </p>
        </div>
      </div>

      {/* Scout Controls */}
      <div className="sov-border bg-[#0A0A0A] p-6 mb-10 grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
        <div>
          <label className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] block mb-2">Corridor</label>
          <select className="input-sovereign cursor-pointer" value={corridor} onChange={(e) => setCorridor(e.target.value)}>
            <option>Both</option><option>Nigeria</option><option>USA</option>
          </select>
        </div>
        <div>
          <label className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] block mb-2">Min Annualized ROI</label>
          <input type="number" min="5" max="100" className="input-sovereign" value={minRoi} onChange={(e) => setMinRoi(e.target.value)} />
        </div>
        <button onClick={runScan} disabled={scanning} data-testid="scouter-run" className="btn-sovereign md:col-span-2">
          {scanning ? <><Loader2 size={14} className="animate-spin" /> <span>Probing the corridor</span></> : <><span>Run Scouter Pass</span> <ArrowUpRight size={14} strokeWidth={1.5} /></>}
        </button>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-6 border-b border-[#D4AF37]/15 pb-4 mb-8">
        {['all', 'New', 'Pursuing', 'Acquired', 'Passed'].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            data-testid={`scouter-filter-${f}`}
            className={`text-[11px] tracking-[0.3em] uppercase transition-colors ${filter === f ? 'text-[#D4AF37]' : 'text-[#A3A3A3] hover:text-[#F5F5F5]'}`}
          >
            {f === 'all' ? `All (${leads.length})` : `${f} (${leads.filter((l) => l.status === f).length})`}
          </button>
        ))}
      </div>

      {/* Leads */}
      {filtered.length === 0 ? (
        <div className="sov-border bg-[#0A0A0A] p-16 text-center text-[#A3A3A3] text-sm">
          No leads in this lane. Run a scouter pass to populate.
        </div>
      ) : (
        <div className="space-y-4">
          {filtered.map((l) => (
            <div key={l.id} data-testid={`scouter-lead-${l.id}`} className="sov-border bg-[#0A0A0A] p-6">
              <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-start">
                <div className="md:col-span-6">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-[10px] tracking-[0.3em] uppercase text-[#D4AF37] border border-[#D4AF37]/40 px-2 py-1">{l.source}</span>
                    <span className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3]">{l.country}</span>
                  </div>
                  <div className="text-lg font-light mb-1">{l.address}</div>
                  <div className="text-xs text-[#A3A3A3] font-light tracking-wide mb-3">{l.location}</div>
                  <p className="text-sm text-[#F5F5F5] font-light leading-relaxed italic">"{l.intel}"</p>
                  {l.flags?.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {l.flags.map((f, i) => (
                        <span key={i} className="text-[9px] tracking-[0.2em] uppercase border border-[#444] text-[#A3A3A3] px-2 py-1">{f}</span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="md:col-span-3 space-y-2">
                  <Row l="Asking" v={fmt$(l.asking_price_usd)} />
                  <Row l="ARV" v={fmt$(l.estimated_arv_usd)} />
                  <Row l="ROI" v={`${l.estimated_roi_pct?.toFixed(1)}%`} gold />
                  <Row l="Hold" v={`${l.holding_months}mo`} />
                </div>
                <div className="md:col-span-3 text-center">
                  <div className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] mb-2">Lead Score</div>
                  <div className="font-display text-5xl text-[#D4AF37]">{l.lead_score}</div>
                  <div className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] mt-3 mb-4">Signal · {l.signal_strength}</div>
                  <div className="flex items-center justify-center gap-2">
                    <span className={`text-[10px] tracking-[0.3em] uppercase px-3 py-1.5 border ${
                      l.status === 'Acquired' ? 'text-[#D4AF37] border-[#D4AF37]' :
                      l.status === 'Passed' ? 'text-[#666] border-[#444]' :
                      l.status === 'Pursuing' ? 'text-[#F5F5F5] border-[#D4AF37]/40' :
                      'text-[#A3A3A3] border-[#444]'
                    }`}>{l.status}</span>
                  </div>
                  <div className="flex justify-center gap-2 mt-3">
                    {l.status !== 'Pursuing' && <ActionBtn icon={Eye} onClick={() => updateStatus(l.id, 'Pursuing')} title="Pursue" />}
                    {l.status !== 'Acquired' && <ActionBtn icon={Check} onClick={() => updateStatus(l.id, 'Acquired')} title="Acquire" />}
                    {l.status !== 'Passed' && <ActionBtn icon={X} onClick={() => updateStatus(l.id, 'Passed')} title="Pass" />}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const Row = ({ l, v, gold }) => (
  <div className="flex justify-between text-xs border-b border-[#D4AF37]/10 pb-1.5">
    <span className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3]">{l}</span>
    <span className={`font-display ${gold ? 'text-[#D4AF37]' : 'text-[#F5F5F5]'}`}>{v}</span>
  </div>
);

const ActionBtn = ({ icon: Icon, onClick, title }) => (
  <button onClick={onClick} title={title}
    className="p-2 border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#050505] transition-colors">
    <Icon size={12} strokeWidth={1.5} />
  </button>
);
